import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { api, ApiError, setCsrf, setTenant, type Principal } from "./api";

interface SessionState {
  loading: boolean;
  principal: Principal | null;
  tenant: string | null;
  login: (apiKey: string) => Promise<void>;
  logout: () => Promise<void>;
  chooseTenant: (slug: string | null) => void;
  can: (perm: string) => boolean;
}

const SessionContext = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [principal, setPrincipal] = useState<Principal | null>(null);
  const [tenant, setTenantState] = useState<string | null>(() => sessionStorage.getItem("sf-tenant"));

  useEffect(() => {
    setTenant(tenant);
  }, [tenant]);

  useEffect(() => {
    setTenant(sessionStorage.getItem("sf-tenant"));
    api<{ csrf_token: string; principal: Principal }>("/v1/session")
      .then((s) => {
        setCsrf(s.csrf_token);
        setPrincipal(s.principal);
      })
      .catch(() => setPrincipal(null))
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (apiKey: string) => {
    const s = await api<{ csrf_token: string }>("/v1/session", { method: "POST", body: { api_key: apiKey } });
    setCsrf(s.csrf_token);
    setPrincipal(await api<Principal>("/v1/whoami"));
  }, []);

  const logout = useCallback(async () => {
    try {
      await api("/v1/session", { method: "DELETE" });
    } catch (e) {
      if (!(e instanceof ApiError)) throw e;
    }
    setCsrf(null);
    setPrincipal(null);
  }, []);

  const chooseTenant = useCallback((slug: string | null) => {
    if (slug) sessionStorage.setItem("sf-tenant", slug);
    else sessionStorage.removeItem("sf-tenant");
    setTenant(slug);
    setTenantState(slug);
  }, []);

  const can = useCallback(
    (perm: string) =>
      !!principal && (principal.is_platform_admin || principal.permissions.includes("admin") || principal.permissions.includes(perm)),
    [principal],
  );

  return (
    <SessionContext.Provider value={{ loading, principal, tenant, login, logout, chooseTenant, can }}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSession(): SessionState {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession outside SessionProvider");
  return ctx;
}
